import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send, ArrowRight, TrendingUp } from "lucide-react";
import { trackPageView, trackCtaClick } from "@/lib/api";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_da4574d6-92b3-44ba-b4b1-9366c9f4fb39/artifacts/2wbblvkq_REVOLUSI%20NUSATRADE%20LOGO-COLOURED%20%281%29.png";

const TELEGRAM_URL = "https://t.me/revolusinusatrade";

// Dummy trader profile DPs (pravatar — Indonesian-feel diverse faces)
const TRADER_AVATARS = [
  "https://i.pravatar.cc/80?img=12",
  "https://i.pravatar.cc/80?img=47",
];

// Animated number counter — runs once when in view
const Counter = ({ to, suffix = "", duration = 2, decimals = 0, startDelay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) =>
    decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString("id-ID")
  );
  const [display, setDisplay] = useState(decimals > 0 ? (0).toFixed(decimals) : "0");

  useEffect(() => {
    if (!inView) return;
    const controls = animate(mv, to, {
      duration,
      delay: startDelay,
      ease: [0.16, 1, 0.3, 1],
    });
    const unsub = rounded.on("change", (v) => setDisplay(v));
    return () => {
      controls.stop();
      unsub();
    };
  }, [inView, to, duration, startDelay, mv, rounded]);

  return (
    <span ref={ref}>
      {display}
      {suffix}
    </span>
  );
};

// Animated candlestick — subtle market visual
const Candlestick = ({ delay = 0, height = 40, bullish = true, x = 0 }) => (
  <motion.g
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 0.35, y: 0 }}
    transition={{ delay, duration: 0.8, ease: "easeOut" }}
    transform={`translate(${x},0)`}
  >
    <line
      x1="6"
      y1={50 - height / 2 - 6}
      x2="6"
      y2={50 + height / 2 + 6}
      stroke={bullish ? "#FDBC3D" : "#ED1C24"}
      strokeWidth="1"
    />
    <rect
      x="0"
      y={50 - height / 2}
      width="12"
      height={height}
      fill={bullish ? "#FDBC3D" : "#ED1C24"}
      opacity="0.85"
    />
  </motion.g>
);

const Hero = () => {
  const navigate = useNavigate();
  const logoClicks = useRef(0);
  const logoTimer = useRef(null);

  useEffect(() => {
    trackPageView("/");
  }, []);

  const handleLogoClick = () => {
    logoClicks.current += 1;
    if (logoTimer.current) clearTimeout(logoTimer.current);
    logoTimer.current = setTimeout(() => {
      logoClicks.current = 0;
    }, 2500);
    if (logoClicks.current >= 5) {
      logoClicks.current = 0;
      clearTimeout(logoTimer.current);
      navigate("/admin/login");
    }
  };

  const handleCtaClick = () => {
    // fire-and-forget; don't block the link navigation
    trackCtaClick("join-telegram");
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "Lead", {
        content_name: "Join Telegram",
        content_category: "CTA",
      });
    }
  };
  const candles = [
    { x: 0, h: 30, b: true },
    { x: 24, h: 50, b: true },
    { x: 48, h: 20, b: false },
    { x: 72, h: 38, b: true },
    { x: 96, h: 55, b: true },
    { x: 120, h: 28, b: false },
    { x: 144, h: 64, b: true },
    { x: 168, h: 42, b: true },
    { x: 192, h: 22, b: false },
    { x: 216, h: 58, b: true },
  ];

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#070405] text-white"
    >
      {/* Background gradient + radial red glow */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(237,28,36,0.35),transparent_55%),radial-gradient(ellipse_at_80%_80%,rgba(33,64,154,0.25),transparent_60%),linear-gradient(180deg,#0a0507_0%,#140608_50%,#070405_100%)]" />
        <div className="absolute inset-0 opacity-[0.07] mix-blend-screen [background-image:radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:3px_3px]" />
      </div>

      {/* Batik kawung pattern overlay */}
      <svg
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full opacity-[0.08]"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="kawung"
            x="0"
            y="0"
            width="80"
            height="80"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="20" cy="20" r="14" fill="none" stroke="#FDBC3D" strokeWidth="1" />
            <circle cx="60" cy="20" r="14" fill="none" stroke="#FDBC3D" strokeWidth="1" />
            <circle cx="20" cy="60" r="14" fill="none" stroke="#FDBC3D" strokeWidth="1" />
            <circle cx="60" cy="60" r="14" fill="none" stroke="#FDBC3D" strokeWidth="1" />
            <circle cx="40" cy="40" r="3" fill="#ED1C24" />
          </pattern>
          <linearGradient id="kawungMask" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="black" stopOpacity="0" />
            <stop offset="40%" stopColor="black" stopOpacity="1" />
            <stop offset="100%" stopColor="black" stopOpacity="0" />
          </linearGradient>
          <mask id="fadeMask">
            <rect width="100%" height="100%" fill="url(#kawungMask)" />
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="url(#kawung)" mask="url(#fadeMask)" />
      </svg>

      {/* Decorative gunungan (mountain) silhouette — left */}
      <motion.svg
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 0.18, x: 0 }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        className="pointer-events-none absolute left-0 top-1/4 -z-10 hidden h-[60%] w-auto md:block"
        viewBox="0 0 200 600"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 0 L140 80 L120 100 L160 160 L130 180 L180 260 L150 290 L190 380 L170 420 L200 500 L160 540 L100 600 L40 540 L0 500 L30 420 L10 380 L50 290 L20 260 L70 180 L40 160 L80 100 L60 80 Z"
          fill="none"
          stroke="#ED1C24"
          strokeWidth="1.5"
        />
      </motion.svg>

      {/* Floating candlestick chart — bottom right */}
      <motion.svg
        viewBox="0 0 240 100"
        className="pointer-events-none absolute bottom-10 right-6 -z-10 hidden h-32 w-72 md:block"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 1 }}
      >
        {candles.map((c, i) => (
          <Candlestick key={i} x={c.x} height={c.h} bullish={c.b} delay={1.4 + i * 0.08} />
        ))}
        <motion.path
          d="M6 60 L30 35 L54 75 L78 50 L102 25 L126 65 L150 18 L174 42 L198 78 L222 30"
          stroke="#FDBC3D"
          strokeWidth="1.2"
          fill="none"
          strokeDasharray="600"
          initial={{ strokeDashoffset: 600 }}
          animate={{ strokeDashoffset: 0 }}
          transition={{ delay: 2.2, duration: 2, ease: "easeInOut" }}
        />
      </motion.svg>

      {/* Floating glow orbs */}
      <motion.div
        aria-hidden
        className="pointer-events-none absolute left-[10%] top-[18%] h-56 w-56 rounded-full bg-[#ED1C24] opacity-30 blur-[100px]"
        animate={{ y: [0, 20, 0], opacity: [0.25, 0.4, 0.25] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="pointer-events-none absolute bottom-[12%] right-[14%] h-72 w-72 rounded-full bg-[#21409A] opacity-25 blur-[120px]"
        animate={{ y: [0, -25, 0], opacity: [0.2, 0.35, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      {/* NAV */}
      <header className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between px-6 py-6 md:px-10">
        <motion.button
          type="button"
          onClick={handleLogoClick}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="group flex items-center gap-3 rounded-full px-1 py-1 transition hover:bg-white/[0.04]"
          data-testid="site-brand"
          aria-label="Revolusi Nusatrade"
        >
          <img
            src={LOGO_URL}
            alt="Revolusi Nusatrade"
            className="h-10 w-10 object-contain drop-shadow-[0_0_12px_rgba(237,28,36,0.55)] transition group-hover:scale-105"
          />
          <span
            className="text-lg tracking-[0.18em] text-white/90"
            style={{ fontFamily: "'Bebas Neue', Impact, sans-serif" }}
          >
            REVOLUSI NUSATRADE
          </span>
        </motion.button>

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="hidden items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-white/70 backdrop-blur-md md:flex"
          data-testid="market-status-pill"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FDBC3D] opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FDBC3D]" />
          </span>
          <TrendingUp className="h-3.5 w-3.5 text-[#FDBC3D]" />
          <span className="font-medium tracking-wide">MARKET INSIGHT · LIVE</span>
        </motion.div>
      </header>

      {/* MAIN HERO */}
      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-6 pb-24 pt-8 md:grid-cols-12 md:gap-8 md:px-10 md:pt-16 lg:gap-16">
        {/* Left: Copy */}
        <div className="md:col-span-7">
          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#ED1C24]/30 bg-[#ED1C24]/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.25em] text-[#FDBC3D]"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#ED1C24]" />
            Komunitas Trader Nusantara
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="text-balance text-5xl leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl xl:text-[5.5rem]"
            style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", letterSpacing: "0.01em" }}
            data-testid="hero-headline"
          >
            Jangan Masuk Market
            <br />
            Tanpa{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-b from-[#FDBC3D] via-[#ED1C24] to-[#ED1C24] bg-clip-text text-transparent">
                Pegangan.
              </span>
              <motion.span
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 1.1, duration: 0.7, ease: "easeOut" }}
                className="absolute -bottom-1 left-0 h-[6px] w-full origin-left rounded-full bg-gradient-to-r from-[#ED1C24] via-[#FDBC3D] to-transparent"
              />
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-8 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg"
            style={{ fontFamily: "'Inter', sans-serif" }}
            data-testid="hero-subheadline"
          >
            Dapatkan <span className="text-white">arahan</span>,{" "}
            <span className="text-white">edukasi</span>, dan{" "}
            <span className="text-white">insight market</span> yang membantu kalian
            entry lebih terarah, jaga modal lebih baik, dan fokus pada proses yang
            konsisten bersama{" "}
            <span className="font-semibold text-[#FDBC3D]">Revolusi Nusatrade</span>.
          </motion.p>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-10 flex flex-col items-start gap-5 lg:flex-row lg:items-center lg:gap-6"
          >
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCtaClick}
              data-testid="cta-join-telegram"
              className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-[#ED1C24] px-8 py-4 text-base font-semibold text-white shadow-[0_10px_40px_-10px_rgba(237,28,36,0.8)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_15px_50px_-10px_rgba(237,28,36,0.95)]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
              <Send className="h-5 w-5" strokeWidth={2.4} />
              <span>Join Telegram</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>

            <div
              className="flex items-center gap-3 text-sm text-white/55"
              style={{ fontFamily: "'Inter', sans-serif" }}
              data-testid="cta-helper"
            >
              <div className="flex -space-x-3">
                {TRADER_AVATARS.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Trader ${i + 1}`}
                    data-testid={`trader-avatar-${i}`}
                    loading="lazy"
                    className="inline-block h-8 w-8 rounded-full border-2 border-[#0a0507] object-cover shadow-[0_2px_8px_rgba(0,0,0,0.6)]"
                  />
                ))}
                <span
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#0a0507] bg-[#ED1C24] text-[10px] font-bold text-white"
                  data-testid="trader-avatar-more"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  +10K
                </span>
              </div>
              <span>
                Bergabung bersama ribuan trader{" "}
                <span className="text-white">Nusantara</span>.
              </span>
            </div>
          </motion.div>

          {/* Stats strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-12 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/10 pt-6"
            data-testid="hero-stats"
          >
            {[
              { v: 10, suffix: "K+", l: "Anggota Aktif", delay: 0 },
              { v: 100, suffix: "%", l: "Edukasi Konsisten", delay: 0.15 },
              { v: 24, suffix: "/7", l: "Insight Market", delay: 0.3 },
            ].map((s, i) => (
              <div key={i} className="flex flex-col" data-testid={`stat-${i}`}>
                <span
                  className="text-3xl text-[#FDBC3D] md:text-4xl"
                  style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                >
                  <Counter to={s.v} suffix={s.suffix} duration={2.2} startDelay={s.delay} />
                </span>
                <span
                  className="mt-1 text-xs uppercase tracking-[0.18em] text-white/55"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {s.l}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Right: Logo */}
        <div className="relative flex items-center justify-center md:col-span-5">
          {/* Rotating ring */}
          <motion.div
            aria-hidden
            className="absolute inset-0 flex items-center justify-center"
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
          >
            <div className="aspect-square w-[110%] max-w-[560px] rounded-full border border-dashed border-[#FDBC3D]/25" />
          </motion.div>
          <motion.div
            aria-hidden
            className="absolute inset-0 flex items-center justify-center"
            animate={{ rotate: -360 }}
            transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
          >
            <div className="aspect-square w-[92%] max-w-[470px] rounded-full border border-[#ED1C24]/20" />
          </motion.div>

          {/* Pulsing glow */}
          <motion.div
            aria-hidden
            className="absolute h-[70%] w-[70%] rounded-full bg-[#ED1C24] opacity-40 blur-[80px]"
            animate={{ scale: [1, 1.12, 1], opacity: [0.3, 0.55, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Logo with float */}
          <motion.img
            src={LOGO_URL}
            alt="Revolusi Nusatrade Garuda Logo"
            data-testid="hero-logo"
            initial={{ opacity: 0, scale: 0.85, rotate: -4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0, y: [0, -14, 0] }}
            transition={{
              opacity: { duration: 1, delay: 0.3, ease: "easeOut" },
              scale: { duration: 1, delay: 0.3, ease: "easeOut" },
              rotate: { duration: 1, delay: 0.3, ease: "easeOut" },
              y: { duration: 5.5, repeat: Infinity, ease: "easeInOut", delay: 1.4 },
            }}
            className="relative z-10 w-full max-w-[420px] drop-shadow-[0_0_45px_rgba(237,28,36,0.45)]"
            style={{ filter: "drop-shadow(0 12px 30px rgba(0,0,0,0.55))" }}
          />

          {/* Floating accent badges around logo */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.6 }}
            className="absolute right-0 top-2 z-20 hidden flex-col gap-2 lg:flex"
            data-testid="floating-stats"
          >
            <div className="rounded-2xl border border-[#FDBC3D]/30 bg-black/75 px-3 py-2 shadow-[0_8px_30px_rgba(0,0,0,0.6)] backdrop-blur-md">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#FDBC3D]" />
                <span
                  className="text-[10px] font-semibold tracking-[0.18em] text-white/85"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  BULLISH SETUP
                </span>
              </div>
              <div
                className="mt-0.5 text-base text-[#FDBC3D]"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                +2.84%
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.6 }}
            className="absolute bottom-2 left-0 z-20 hidden rounded-2xl border border-[#21409A]/40 bg-black/75 px-3 py-2 shadow-[0_8px_30px_rgba(0,0,0,0.6)] backdrop-blur-md lg:block"
            data-testid="badge-risk"
          >
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-[#21409A]" />
              <span
                className="text-[10px] font-semibold tracking-[0.18em] text-white/85"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                RISK MANAGED
              </span>
            </div>
            <div
              className="mt-0.5 text-base text-white"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              JAGA MODAL
            </div>
          </motion.div>
        </div>
      </div>

      {/* Marquee ticker bottom */}
      <div
        className="relative z-10 border-t border-white/10 bg-black/30 py-3 backdrop-blur-sm"
        data-testid="hero-ticker"
      >
        <div className="flex overflow-hidden">
          <motion.div
            className="flex shrink-0 items-center gap-10 whitespace-nowrap pr-10 text-sm text-white/70"
            style={{ fontFamily: "'Inter', sans-serif" }}
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
          >
            {Array.from({ length: 2 }).map((_, k) => (
              <div key={k} className="flex shrink-0 items-center gap-10">
                {[
                  ["BTC/USDT", "+1.42%", "#FDBC3D"],
                  ["ETH/USDT", "+0.86%", "#FDBC3D"],
                  ["IDX COMPOSITE", "−0.32%", "#ED1C24"],
                  ["XAU/USD", "+0.21%", "#FDBC3D"],
                  ["BBCA", "+2.10%", "#FDBC3D"],
                  ["TLKM", "−0.45%", "#ED1C24"],
                  ["EUR/USD", "+0.07%", "#FDBC3D"],
                  ["BBRI", "+1.18%", "#FDBC3D"],
                ].map(([sym, val, color], i) => (
                  <span key={i} className="flex items-center gap-2">
                    <span className="text-white/90">{sym}</span>
                    <span style={{ color }} className="font-semibold">
                      {val}
                    </span>
                    <span className="text-white/20">•</span>
                  </span>
                ))}
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
